import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Column order/visibility/width + drag-reorder + resize state for NestedDocumentTable.
 * Extracted 1:1 from NestedDocumentTable.tsx (CLAUDE.md rule 9 split) — no behaviour change.
 */
export function useNestedDocumentColumns() {
  const { t } = useTranslation();

  // --- Column State ---
  const [columnOrder, setColumnOrder] = React.useState<string[]>(['ident', 'name', 'unitMeasurement', 'purchasePrice', 'valuationPrice', 'quantity', 'quantityOS', 'quantityWorkOrder', 'serial', 'deadline', 'priceValue']);
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>([]);
  const [columnWidths, setColumnWidths] = React.useState<{ [key: string]: number }>({});

  // Column configuration mapping
  const columnConfig = {
    ident: { label: t("tableHeaders.idents"), width: '7%' },
    name: { label: t("tableHeaders.name"), width: '15%' },
    unitMeasurement: { label: t("nestedTable.unitMeasurement"), width: '6%' },
    purchasePrice: { label: t('nestedTable.purchasePrice'), width: '9%' },
    valuationPrice: { label: t('nestedTable.valuationPrice'), width: '9%' },
    quantity: { label: t('nestedTable.quantity'), width: '9%' },
    quantityOS: { label: t('nestedTable.quantityOS'), width: '9%' },
    quantityWorkOrder: { label: t('nestedTable.quantityWorkOrder'), width: '8%' },
    serial: { label: t('nestedTable.serial'), width: '8%' },
    deadline: { label: t('nestedTable.deadline'), width: '8%' },
    priceValue: { label: t('nestedTable.priceValue'), width: '12%' }
  };

  // --- Refactored drag-and-drop logic (TableHeader pattern) ---
  const [draggedColumn, setDraggedColumn] = React.useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = React.useState<string | null>(null);

  // --- Column resize functionality ---
  const [resizing, setResizing] = React.useState<string | null>(null);
  const [startX, setStartX] = React.useState<number>(0);
  const [startWidth, setStartWidth] = React.useState<number>(0);

  const handleDragStart = (e: React.DragEvent, column: string) => {
    setDraggedColumn(column);
    e.dataTransfer.setData("text/plain", column);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, column: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(column);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumn: string) => {
    e.preventDefault();
    if (!draggedColumn) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumn);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      setColumnOrder(newOrder);
    }

    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // --- Column resize handlers ---
  const handleResizeMove = React.useCallback((e: MouseEvent) => {
    if (!resizing) return;

    const deltaX = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + deltaX);
    handleColumnResize(resizing, newWidth);
  }, [resizing, startX, startWidth]);

  const handleResizeEnd = React.useCallback(() => {
    setResizing(null);
    setStartX(0);
    setStartWidth(0);
  }, []);

  const handleResizeStart = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(column);
    setStartX(e.clientX);
    setStartWidth(columnWidths?.[column] || 150);
  };

  // Add global mouse event listeners for resizing
  React.useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // --- Column Manager Functions ---
  const handleColumnReorder = (newOrder: string[]) => {
    setColumnOrder(newOrder);
  };

  const handleColumnHide = (columnKey: string) => {
    setHiddenColumns(prev => [...prev, columnKey]);
  };

  const handleColumnShow = (columnKey: string) => {
    setHiddenColumns(prev => prev.filter(col => col !== columnKey));
  };

  const handleColumnToggle = (columnKey: string) => {
    if (hiddenColumns.includes(columnKey)) {
      handleColumnShow(columnKey);
    } else {
      handleColumnHide(columnKey);
    }
  };

  const handleColumnResize = (column: string, width: number) => {
    setColumnWidths(prev => ({
      ...prev,
      [column]: width
    }));
  };

  return {
    columnOrder,
    hiddenColumns,
    columnWidths,
    columnConfig,
    draggedColumn,
    dragOverColumn,
    resizing,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleResizeStart,
    handleColumnReorder,
    handleColumnHide,
    handleColumnShow,
    handleColumnToggle,
    handleColumnResize,
  };
}

export default useNestedDocumentColumns;
